import React from "react";
import _ from "lodash";
import { format as dateFormat } from "date-fns";
import { de, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import getOptaCountryCode from "../../../../../shared/utils/getOptaCountryCode";
import {
  getDynamicLogoPath,
  toIsoDateString,
} from "../../../../../shared/utils/functions";
import ImageComponent from "../../../../../shared/components/Image";
import { getTimeInZone } from "../../../../../shared/utils/dateHelpers";


const Image = ImageComponent?.default || ImageComponent;

function isClubWorldCup(competitionId) {
  return competitionId === "dc4k1xh2984zbypbnunk7ncic";
}

function hasTeamIdentity(team, competitionId) {
  if (!team?.id && !team?.code) {
    return false;
  }

  return Boolean(
    (isClubWorldCup(competitionId) && team?.id) ||
      getOptaCountryCode(team?.name, team?.code),
  );
}

function TeamFlag({ team, competitionId, className }) {
  console.log('team: ', team)
  const alpha2 = getOptaCountryCode(team?.name, team?.code);
  const showFlag = hasTeamIdentity(team, competitionId);

  return (
    <div className={`${className} ${alpha2 ? "no-flag-border" : ""}`}>
      {showFlag ? (
        Image ? (
          <Image
            src={getDynamicLogoPath({
              team: {
                name: team?.name,
                code: team?.code,
                id: team?.id,
                ThreeLetterCode: team?.code,
              },
              competitionId,
            })}
          />
        ) : (
          <img
            alt=""
            src={getDynamicLogoPath({
              team: {
                name: team?.name,
                code: team?.code,
                id: team?.id,
                ThreeLetterCode: team?.code,
              },
              competitionId,
            })}
          />
        )
      ) : (
        "?"
      )}
    </div>
  );
}

const MatchRow = ({
  matchday,
  index,
  competitionId,
  variant = "default",
  showDebug = false,
}) => {
  const { t, i18n } = useTranslation();
  const lng = i18n?.language;

  const home = matchday?.matchInfo?.contestant?.[0];
  const away = matchday?.matchInfo?.contestant?.[1];
  const matchDateSource =
    matchday?.matchInfo?.plannedKickoffTime ||
    toIsoDateString(matchday?.matchInfo?.date, matchday?.matchInfo?.time);
  const matchDate = matchDateSource ? new Date(matchDateSource) : null;
  const time = matchday?.matchInfo?.time
    ? getTimeInZone(matchDate, lng, true)
    : "-";

  const matchStatus = _.toLower(
    matchday?.liveData?.matchDetails?.matchStatus,
  );
  const hasScore =
    matchStatus === "played" || matchStatus === "playing";
  const scores = matchday?.liveData?.matchDetails?.scores?.total;
  const homeCode = home?.code && home.code !== "tbd." ? home.code : "-";
  const awayCode = away?.code && away.code !== "tbd." ? away.code : "-";

  const timeBlock =
    matchStatus === "playing" && LiveStatusBadge ? (
      <div className="row-score-status">
        {/* <LiveStatusBadge /> */}
        playing
      </div>
    ) : (
      <div className="match-time">
        <div className="row-day">
          {matchDate
            ? dateFormat(
                matchDate,
                lng === "de" ? "dd.MM.yyyy" : "MM/dd/yyyy",
                { locale: lng === "de" ? de : enUS },
              )
            : "-"}
          {matchday?.matchInfo?.time ? (
            <>
              &nbsp;{time}
              <div className="timezone-time">
                {t("TIME", { keyPrefix: "templates" })}
              </div>
            </>
          ) : null}
        </div>
      </div>
    );

  if (variant === "final") {
    return (
      <div
        key={matchday?.id || index}
        className={`group-row group-row-index-${index}`}
      >
        <div className="match-block">
          {showDebug ? (
            <div className="mid-indicator">
              <small>MID:{matchday?.matchInfo?.id}</small>
            </div>
          ) : null}
          <div className="match-label-final">
            <div className="green-glow">{t("Final")}</div>
          </div>
          <div className="final-team-wrap">
            <div className="final-team-info-wrap">
              <div className="final-team-info-container">
                <TeamFlag
                  team={home}
                  competitionId={competitionId}
                  className="team-home"
                />
                <div className="team-name home">
                  {homeCode !== "-"
                    ? t(homeCode, { ns: "countries" })
                    : "-"}
                </div>
              </div>
            </div>
            <div className="final-caret">
              {hasScore
                ? `${scores?.home || 0} : ${scores?.away || 0}`
                : "-:-"}
            </div>
            <div className="final-team-info-wrap">
              <div className="final-team-info-container">
                <TeamFlag
                  team={away}
                  competitionId={competitionId}
                  className="team-away"
                />
                <div className="team-name away">
                  {awayCode !== "-"
                    ? t(awayCode, { ns: "countries" })
                    : "-"}
                </div>
              </div>
            </div>
          </div>
          {timeBlock}
        </div>
      </div>
    );
  }

  if (variant === "third") {
    return (
      <div
        key={matchday?.id || index}
        className={`group-row group-row-index-${index}`}
      >
        <div className="match-block">
          <div className="match-label">
            <div className="green-glow">
              3<sup>rd</sup> Place
            </div>
          </div>
          <div className="final-team-wrap">
            <div className="final-team-info-wrap">
              <div className="final-team-info-container">
                <TeamFlag
                  team={home}
                  competitionId={competitionId}
                  className="team-home"
                />
                <div className="team-name home">
                  {homeCode !== "-"
                    ? t(homeCode, { ns: "countries" })
                    : "-"}
                </div>
              </div>
            </div>
            <div className="final-caret">
              {hasScore
                ? `${scores?.home || 0} : ${scores?.away || 0}`
                : "-:-"}
            </div>
            <div className="final-team-info-wrap">
              <div className="final-team-info-container">
                <TeamFlag
                  team={away}
                  competitionId={competitionId}
                  className="team-away"
                />
                <div className="team-name away">
                  {awayCode !== "-"
                    ? t(awayCode, { ns: "countries" })
                    : "-"}
                </div>
              </div>
            </div>
          </div>
          {timeBlock}
        </div>
      </div>
    );
  }

  return (
    <div
      key={matchday?.id || index}
      className={`group-row group-row-index-${index}`}
    >
      <div className="match-block">
        {showDebug ? (
          <div className="mid-indicator">
            <small>MID:{matchday?.matchInfo?.id}</small>
            <br />
            <small>NID:{matchday?.matchInfo?.nextMatchWinnerId}</small>
          </div>
        ) : null}
        <div className="team-info-wrap">
          <div className="team-info-container">
            <TeamFlag
              team={home}
              competitionId={competitionId}
              className="team-home"
            />
            <div className="team-name home">
              {homeCode !== "-" ? t(homeCode, { ns: "countries" }) : "-"}
            </div>
          </div>
          <div className="score-home">
            {hasScore ? `${scores?.home || 0}` : "-"}
          </div>
        </div>
        <div className="team-info-divider" />
        <div className="team-info-wrap">
          <div className="team-info-container">
            <TeamFlag
              team={away}
              competitionId={competitionId}
              className="team-away"
            />
            <div className="team-name away">
              {awayCode !== "-" ? t(awayCode, { ns: "countries" }) : "-"}
            </div>
          </div>
          <div className="score-away">
            {hasScore ? `${scores?.away || 0}` : "-"}
          </div>
        </div>
        {timeBlock}
      </div>
    </div>
  );
};

export default MatchRow;
